import { Form } from 'antd'
import { isArray } from 'lodash'
import moment from 'moment'
import { useTranslation } from 'next-i18next'
import { useEffect, useMemo, useState } from 'react'
import classApi from 'src/api/classApi'
import sessionApi from 'src/api/sessionApi'
import { ModalForm } from 'src/components/modal/ModalForm'
import { useGetData } from 'src/hooks/useGetData'
import { usePaginationFilter } from 'src/hooks/usePaginationFilter'
import { useValidateQuerySearch } from 'src/hooks/useValidateQuerySearch'
import { isPastTimeV2 } from 'src/libs/util'
import { FilterTeacher } from './FilterTeacher'
import { TeacherTable } from './TableTeacher'
import { FULL_TEACHER_GRADE_LEVEL } from 'src/libs/constant/grade'
import { handleBetween, handleCommon } from 'src/libs/filterUtil'

const searchSchema = {
  page: {
    required: true,
    default: 1,
    test: (value) => {
      return /^\d+$/.test(value)
    }
  },
  size: {
    required: true,
    default: 50,
    test: (value) => Number(value) <= 100
  },
  id: true
}

function getAvailableTeacherSchedules (teacherSchedules, requiredScheduleDates) {
  if (!isArray(teacherSchedules)) return []

  let availableSchedules = []

  const teacherSchedulesSorted = teacherSchedules
    .filter(schedule => !schedule?.id || !isPastTimeV2({ date: schedule.scheduleDate, time: schedule.slotStartTime }))
    .sort((scheduleA, scheduleB) => scheduleA.scheduleDate - scheduleB.scheduleDate)
  let scheduleNotOpenLastestIndex = -1

  teacherSchedulesSorted.forEach((teacherSchedule, index) => {
    if (teacherSchedule.status !== 'OPEN') {
      scheduleNotOpenLastestIndex = index
    }
  })

  availableSchedules = scheduleNotOpenLastestIndex >= 0 ? teacherSchedulesSorted.slice(scheduleNotOpenLastestIndex + 1) : teacherSchedulesSorted

  if (isArray(requiredScheduleDates) && requiredScheduleDates?.length) {
    const isHaveRequiredScheduleDate = availableSchedules.find((teacherSchedule) => requiredScheduleDates.includes(teacherSchedule.scheduleDate))
    return isHaveRequiredScheduleDate ? availableSchedules : []
  }

  return availableSchedules
}

const BETWEEN = [
  { field: 'hireDate', min: 'hireDateMin', max: 'hireDateMax', type: 'Int' }
]
const GREATER_THAN_OR_EQUAL = [{ field: 'hireDate', type: 'Int' }]
const LESS_THAN_OR_EQUAL = [{ field: 'hireDate', type: 'Int' }]
/**
 *
 * @param {Object} props
 * @param {Object} props.filterTeacher - Data bộ lọc
 * @param {Function} props.handleClose - callback sau khi close
 * @param {Boolean} props.isDisabledFilterGrade - Ẩn / hiện trường filter grade trên bộ lọc
 * @param {Boolean} props.isDisabledFilterSchedueleType - Ẩn / hiện trường filter loại lịch (schedule type) trên bộ lọc
 * @param {Boolean} props.isNeedCheckConsecutiveSchedules - Thực hiện kiểm tra lấy lịch gv trống liên tiếp
 * @param {Boolean} props.isNeedHaveScheduleDateInConsecutiveSchedules - Thực hiện kiểm tra lịch gv trống liên tiếp từ ngày scheduleDate trong filterTeacher
 * @returns
 */
export default function ModalSelectTeacherOfSession ({
  filterTeacher,
  handleClose,
  isDisabledFilterGrade = false,
  isDisabledFilterSchedueleType = false,
  isNeedCheckConsecutiveSchedules = false,
  isNeedHaveScheduleDateInConsecutiveSchedules = false,
  setTeacherSchedule,
  studentIds,
  classData
}) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [criteria, setCriteria] = useState()
  const { queryData } = useValidateQuerySearch(searchSchema)

  const criteriaClassify = useMemo(() => {
    return {
      criteria: [],
      page: 0,
      size: 50
    }
  }, [])

  const { data: teacherClassify } = useGetData(
    classApi.getTeacherClassify,
    criteriaClassify
  )

  const { paginatedData, status } = usePaginationFilter(
    sessionApi.listTeacherRegisteredSchedule,
    criteria,
    isNeedCheckConsecutiveSchedules ? getNextWeeksTeacherSchedules : null,
    {
      isDisabled: !criteria?.length,
      queryString: {
        startDate: filterTeacher?.scheduleDate || classData?.learnDate,
        endDate: filterTeacher?.scheduleDate || classData?.learnDate,
        ...(classData?.levelId && { studentLevelId: classData?.levelId }),
        density: form.getFieldValue('density') || form.getFieldValue('density') === false ? form.getFieldValue('density') : undefined
      },
      studyProgramId: classData?.studyProgramId
    }
  )

  const onChangeFilterField = () => {
    const values = form.getFieldsValue()
    const { scheduleType, density, hireDateMin, hireDateMax, ...formValues } = values
    const filterCriteria = [
      {
        field: 'status',
        operator: 'EQUAL',
        value: 'OPEN'
      },
      {
        field: 'isAvailableWithSession',
        operator: 'EQUAL_OR_NULL',
        value: 'true',
        type: 'Boolean'
      }
      // {
      //   field: "violationLevelId",
      //   operator: "IS_NULL"
      // }
    ]

    Object.keys(formValues).forEach((key) => {
      if (values[key]) {
        if (key === 'scheduleType') {
          filterCriteria.push({
            field: key,
            operator: 'IN',
            values: [values[key]]
          })
        } else if (key === 'teacherId') {
          filterCriteria.push({
            field: key,
            operator: 'EQUAL',
            value: values[key],
            type: 'Int'
          })
        } else if (key === 'grades') {
          filterCriteria.push({
            field: 'grades',
            operator: 'IN',
            values: JSON.parse(values[key])
          })
        } else {
          filterCriteria.push({
            field: key,
            operator: 'EQUAL',
            value: values[key]
          })
        }
      }
    })
    if (hireDateMin && !hireDateMax) {
      const hireDatePayload = { hireDate: hireDateMin.format('YYYYMMDD') }
      filterCriteria.push(...handleCommon(hireDatePayload, GREATER_THAN_OR_EQUAL, 'GREATER_THAN_OR_EQUAL'))
    } else if (hireDateMax && !hireDateMin) {
      const hireDatePayload = { hireDate: hireDateMax.format('YYYYMMDD') }
      filterCriteria.push(...handleCommon(hireDatePayload, LESS_THAN_OR_EQUAL, 'LESS_THAN_OR_EQUAL'))
    } else if (hireDateMax && hireDateMin) {
      const hireDatePayload = {
        hireDateMin: hireDateMin.format('YYYYMMDD'),
        hireDateMax: hireDateMax.format('YYYYMMDD')
      }
      filterCriteria.push(...handleBetween(hireDatePayload, BETWEEN))
    }
    const { scheduleDate, classTimeSlotId, dayOfWeek, grades, levelId } =
      filterTeacher

    if (filterTeacher.scheduleType) {
      filterCriteria.push({
        field: 'scheduleType',
        operator: 'IN',
        values: [filterTeacher.scheduleType]
      })
    }

    if (scheduleDate) {
      filterCriteria.push({
        field: 'scheduleDate',
        operator: 'EQUAL',
        // value: scheduleDate.format('YYYYMMDD'),
        value: scheduleDate,
        type: 'Int'
      })
    }
    if (grades) {
      const _grades = FULL_TEACHER_GRADE_LEVEL.find(val => val.includes(grades))
      filterCriteria.push({
        field: 'grades',
        operator: 'IN',
        values: _grades
      })
    }
    if (levelId) {
      filterCriteria.push({
        field: 'levelId',
        operator: 'EQUAL',
        values: [levelId]
      })
    }

    if (classTimeSlotId) {
      filterCriteria.push({
        field: 'classTimeSlotId',
        operator: 'EQUAL',
        value: classTimeSlotId
      })
    }
    if (dayOfWeek) {
      filterCriteria.push({
        field: 'dayOfWeek',
        operator: 'EQUAL',
        value: dayOfWeek
      })
    }
    if (!scheduleType && !filterTeacher.scheduleType) {
      filterCriteria.push({
        field: 'scheduleType',
        operator: 'IN',
        values: ['REGULAR', 'BACKUP']
      })
    }
    // if (isArray(studentIds) && studentIds.length > 0) {
    //   filterCriteria.push(
    //     {
    //       field: 'studentKeepingScheduleOr',
    //       operator: 'OR_MULTIPLE_FIELDS',
    //       childList: [
    //         {
    //           field: 'studentKeepingSchedule',
    //           operator: 'IS_NULL'
    //         },
    //         {
    //           field: 'studentKeepingSchedule',
    //           operator: 'IN',
    //           values: studentIds,
    //           type: 'Long'
    //         }
    //       ]
    //     }
    //   )
    // }
    const criteriaNew = [
      {
        field: 'mainAndGroup',
        operator: 'AND_MULTIPLE_FIELDS',
        childList: filterCriteria
      }
    ]
    setCriteria(criteriaNew)
  }

  useEffect(() => {
    if (!filterTeacher) return
    onChangeFilterField()
  }, [filterTeacher, studentIds])

  const teacherSchedules = useMemo(() => {
    if (!isNeedCheckConsecutiveSchedules) return paginatedData

    const currentDate = moment().format('YYYYMMDD')
    const currentTime = new Date().getHours() * 60 + new Date().getMinutes()

    const requiredScheduleDates = isNeedHaveScheduleDateInConsecutiveSchedules && filterTeacher.scheduleDate ? [filterTeacher.scheduleDate] : false

    const _newPaginatedData = paginatedData?.content.reduce((avaiableTeacherSchedules, teacherSchedule) => {
      if (+teacherSchedule.scheduleDate > +currentDate || (+teacherSchedule.scheduleDate === +currentDate && +teacherSchedule.slotStartTime > +currentTime)) {
        const availableTeacherSchedule = getAvailableTeacherSchedules(
          teacherSchedule?.schedules?.content,
          requiredScheduleDates
        )

        if (availableTeacherSchedule?.length) {
          return [...avaiableTeacherSchedules, { ...teacherSchedule, schedules: { content: availableTeacherSchedule } }]
        }
      }
      return avaiableTeacherSchedules
    }, [])
    return { ...paginatedData, content: _newPaginatedData }
  }, [paginatedData])
  const onReset = () => {
    form.resetFields()
    onChangeFilterField()
  }
  return (
    <ModalForm
      form={form}
      visible
      title={t('page.class.text.select_teacher')}
      onCancel={handleClose}
      destroyOnClose
      forceRender
      footer={null}
      width={1250}
    >
      <FilterTeacher
        form={form}
        onChangeFilterField={onChangeFilterField}
        teacherClassify={teacherClassify}
        isDisabledFilterGrade={isDisabledFilterGrade || !!filterTeacher?.grades}
        isDisabledFilterSchedueleType={isDisabledFilterSchedueleType || !!filterTeacher?.scheduleType}
        onReset={onReset}
      />
      {teacherClassify && <TeacherTable
        loading={status === 'loading'}
        handleClose={handleClose}
        setTeacherSchedule={setTeacherSchedule}
        queryData={queryData}
        paginatedData={teacherSchedules}
        criteria={criteria}
        teacherClassify={teacherClassify}
      />}
    </ModalForm>
  )
}

export function getNextWeeksTeacherSchedules (initialPaginatedData) {
  const currentDate = moment().format('YYYYMMDD')
  const content = initialPaginatedData?.content?.map?.(item => ({
    ...item,
    propertyId: item?.id,
    detailApi: sessionApi.listTeacherSchedule,
    detailApiParams: [{
      criteria: [
        { field: 'classTimeSlotId', operator: 'EQUAL', value: item.classTimeSlotId },
        { field: 'teacherId', operator: 'EQUAL', value: item.teacherId, type: 'Long' },
        { field: 'dayOfWeek', operator: 'EQUAL', value: item.dayOfWeek },
        {
          field: 'scheduleDate',
          operator: 'GREATER_THAN_OR_EQUAL',
          value: item.scheduleDate || currentDate,
          type: 'Int'
        },
        { field: 'scheduleType', operator: 'EQUAL', value: 'REGULAR' },
        { field: 'status', operator: 'IN', values: ['OPEN', 'ASSIGNED'] }
      ],
      sortOrders: [{ field: 'scheduleDate', isASC: true }],
      page: 0,
      size: 10
    }]
  }))
  return effect({ ...initialPaginatedData, content }, { property: 'schedules', propertyId: 'propertyId' })
}